import React, { useEffect, useMemo, useRef, useState } from 'react';
import { MessageCircle, Printer, RefreshCcw, Search, X, Users, Phone, Pencil, Save } from 'lucide-react';
import toast from 'react-hot-toast';
import { supabase } from '../supabase';
import { useAuth } from '../context/AuthContext';
import { applyActiveProductsFilter } from '../utils/productStatus';
import { fetchAllRows } from '../utils/supabasePagination';
import {
  buildPriceListExportRows,
  printPriceList,
  sendPriceListWhatsApp,
  normalizeWhatsAppPhone,
  isMobileDevice,
  getPriceListPrintConfig,
  PRICE_LIST_DOC_TYPE,
} from '../utils/priceListExport';

// Lo escrito en el buscador es un teléfono (sin letras y con dígitos suficientes).
const phoneFromSearch = (text) => {
  const value = String(text || '').trim();
  if (!value || /[a-zA-ZñÑ]/.test(value)) return '';
  return normalizeWhatsAppPhone(value);
};

/**
 * Imprimir / mandar por WhatsApp una lista de precios.
 * - Desde el form recibe `products` y la lista en borrador (lo que se ve).
 * - Desde el listado no recibe productos: los trae al abrir.
 * WhatsApp: se manda el PDF como archivo. Sin número, en celular: menú de
 * compartir con el PDF adjunto. Con número (cliente o tipeado en el buscador):
 * se descarga el PDF y se abre el chat de ese número con el mensaje escrito.
 * El mensaje se puede editar antes de mandar y guardar como predeterminado.
 */
export default function PriceListShareModal({ open, onClose, priceList, products, organization, orgId }) {
  const [loadedProducts, setLoadedProducts] = useState(null);
  const [loading, setLoading] = useState(false);
  const [listClients, setListClients] = useState([]);
  const [searchResults, setSearchResults] = useState([]);
  const [clientSearch, setClientSearch] = useState('');
  const [selectedClient, setSelectedClient] = useState(null);
  const [phone, setPhone] = useState('');
  const [savePhone, setSavePhone] = useState(true);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');
  const [editingMessage, setEditingMessage] = useState(false);
  const [savingDefault, setSavingDefault] = useState(false);
  const searchSeq = useRef(0);
  const { refreshOrganization } = useAuth();
  const defaultMessage = getPriceListPrintConfig(organization).whatsappMessage || '';

  useEffect(() => {
    if (!open) return;
    setPhone('');
    setClientSearch('');
    setSearchResults([]);
    setSelectedClient(null);
    setSavePhone(true);
    setMessage(getPriceListPrintConfig(organization).whatsappMessage || '');
    setEditingMessage(false);

    if (orgId && priceList?.id) {
      supabase
        .from('clients')
        .select('id, full_name, phone, price_list_id')
        .eq('organization_id', orgId)
        .eq('price_list_id', priceList.id)
        .not('is_active', 'is', false)
        .order('full_name', { ascending: true })
        .limit(100)
        .then(({ data }) => setListClients(data || []));
    } else {
      setListClients([]);
    }

    if (Array.isArray(products) || !orgId) return;
    setLoading(true);
    fetchAllRows(
      () => applyActiveProductsFilter(
        supabase
          .from('products')
          .select('id, name, brand, barcode, sell_price, cost_price, priced_in_usd, price_usd')
          .eq('organization_id', orgId)
      ),
      { label: 'listaPreciosExport' }
    )
      .then(({ data, error }) => {
        if (error) {
          console.error(error);
          toast.error('No se pudo cargar el catálogo');
          setLoadedProducts([]);
        } else {
          setLoadedProducts(data || []);
        }
      })
      .finally(() => setLoading(false));
    // organization fuera de deps: el mensaje se inicializa al abrir, no al refrescar.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, orgId, priceList?.id, products]);

  // Búsqueda de cualquier cliente de la empresa.
  useEffect(() => {
    if (!open || !orgId) return undefined;
    const q = clientSearch.trim();
    if (q.length < 2) {
      setSearchResults([]);
      return undefined;
    }
    const seq = ++searchSeq.current;
    const isNumeric = !/[a-zA-ZñÑ]/.test(q);
    const term = q.replace(/[%_,()]/g, '');
    const t = setTimeout(async () => {
      let query = supabase
        .from('clients')
        .select('id, full_name, phone, price_list_id')
        .eq('organization_id', orgId)
        .not('is_active', 'is', false);
      // Número tipeado: también busca clientes con ese teléfono.
      query = isNumeric
        ? query.or(`full_name.ilike.%${term}%,phone.ilike.%${term}%`)
        : query.ilike('full_name', `%${term}%`);
      const { data } = await query
        .order('full_name', { ascending: true })
        .limit(30);
      if (seq === searchSeq.current) setSearchResults(data || []);
    }, 250);
    return () => clearTimeout(t);
  }, [clientSearch, open, orgId]);

  const rows = useMemo(() => {
    const source = Array.isArray(products) ? products : loadedProducts;
    if (!open || !priceList || !Array.isArray(source)) return [];
    return buildPriceListExportRows(source, priceList, organization?.usd_exchange_rate);
  }, [open, priceList, products, loadedProducts, organization?.usd_exchange_rate]);

  const shownClients = clientSearch.trim().length >= 2 ? searchResults : listClients;
  const typedPhone = phoneFromSearch(clientSearch);

  if (!open) return null;

  const ensureRows = () => {
    if (rows.length > 0) return true;
    toast.error('La lista no tiene productos con precio para mostrar');
    return false;
  };

  const handlePrint = () => {
    if (!ensureRows()) return;
    const result = printPriceList({ priceList, rows, organization });
    if (result === 'downloaded') toast('El navegador bloqueó la ventana: se descargó el PDF', { icon: 'ℹ️' });
  };

  const send = async ({ targetPhone, clientName, client }) => {
    if (!ensureRows()) return;
    if (!orgId) return toast.error('Sesión inválida');
    setBusy(true);
    try {
      // Sin awaits antes: compartir / abrir ventana necesitan el gesto del click.
      const result = await sendPriceListWhatsApp({
        priceList, rows, organization, phone: targetPhone, clientName, template: message,
      });

      if (client && savePhone && targetPhone && !normalizeWhatsAppPhone(client.phone)) {
        const { error } = await supabase.from('clients').update({ phone: String(targetPhone).trim() })
          .eq('id', client.id).eq('organization_id', orgId);
        if (error) console.warn('No se pudo guardar el teléfono del cliente', error);
      }

      if (result === 'cancelled') return;
      if (result === 'shared') {
        toast.success('PDF compartido');
      } else if (result === 'download_chat') {
        toast.success(
          targetPhone
            ? `Se descargó el PDF y se abrió el chat de ${clientName || targetPhone}: arrastrá el archivo y enviá`
            : 'Se descargó el PDF: elegí el contacto en WhatsApp, arrastrá el archivo y enviá',
          { duration: 8000 }
        );
      } else {
        toast('Se descargó el PDF. El navegador bloqueó WhatsApp: abrilo y adjuntá el archivo.', { icon: 'ℹ️', duration: 8000 });
      }
      onClose?.();
    } catch (error) {
      console.error(error);
      toast.error('No se pudo generar el PDF');
    } finally {
      setBusy(false);
    }
  };

  const saveDefaultMessage = async () => {
    const text = String(message || '').trim();
    if (!text || !orgId) return;
    setSavingDefault(true);
    try {
      // Se relee print_config de la base para no pisar cambios de otra pestaña.
      const { data, error: readError } = await supabase
        .from('organizations').select('print_config').eq('id', orgId).single();
      if (readError) throw readError;
      const current = data?.print_config || {};
      const next = {
        ...current,
        [PRICE_LIST_DOC_TYPE]: { ...(current[PRICE_LIST_DOC_TYPE] || {}), whatsappMessage: text },
      };
      const { error } = await supabase.from('organizations').update({ print_config: next }).eq('id', orgId);
      if (error) throw error;
      await refreshOrganization?.();
      setEditingMessage(false);
      toast.success('Mensaje guardado como predeterminado');
    } catch (error) {
      console.error(error);
      toast.error('No se pudo guardar el mensaje (¿tenés permiso de configuración?)');
    } finally {
      setSavingDefault(false);
    }
  };

  const handleClientClick = (client) => {
    if (normalizeWhatsAppPhone(client.phone)) {
      send({ targetPhone: client.phone, clientName: client.full_name, client });
    } else {
      setSelectedClient(client);
      setPhone('');
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/55 backdrop-blur-sm p-4" onClick={onClose}>
      <div
        className="w-full max-w-md bg-white rounded-3xl shadow-2xl overflow-hidden max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="bg-[#0B2A36] px-5 py-4 flex items-center justify-between">
          <div className="min-w-0">
            <p className="text-[9px] font-black uppercase tracking-widest text-[#3FF2AA]">Compartir lista</p>
            <h3 className="text-sm font-black uppercase text-white truncate">{priceList?.name || 'SIN NOMBRE'}</h3>
          </div>
          <button type="button" onClick={onClose} className="p-2 rounded-xl text-white/70 hover:text-white" title="Cerrar">
            <X size={18} />
          </button>
        </div>

        <div className="p-5 space-y-4 overflow-y-auto">
          <p className="text-[10px] font-black uppercase tracking-widest text-slate-400">
            {loading ? 'Cargando productos…' : `${rows.length} producto${rows.length === 1 ? '' : 's'} con precio · sin costos ni ocultos`}
          </p>

          {/* ── Mensaje que acompaña al PDF ── */}
          <div className="rounded-2xl border border-slate-100 bg-slate-50/60 p-3 space-y-2">
            <div className="flex items-center justify-between gap-2">
              <span className="text-[9px] font-black uppercase tracking-widest text-slate-500">Mensaje de WhatsApp</span>
              {!editingMessage && (
                <button type="button" onClick={() => setEditingMessage(true)} className="inline-flex items-center gap-1 text-[9px] font-black uppercase tracking-widest text-[#51A7FD] hover:underline">
                  <Pencil size={11} /> Editar
                </button>
              )}
            </div>
            {editingMessage ? (
              <>
                <textarea
                  rows={3}
                  autoFocus
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  className="w-full bg-white border border-slate-200 rounded-xl p-2 text-[11px] font-medium text-slate-700 outline-none focus:border-[#51A7FD] resize-y"
                />
                <p className="text-[9px] text-slate-400 leading-snug">
                  Variables: {'{cliente}'} {'{lista}'} {'{empresa}'} {'{fecha}'}. Se usa para este envío.
                </p>
                <div className="flex flex-wrap gap-2">
                  {message.trim() && message.trim() !== defaultMessage.trim() && (
                    <button
                      type="button"
                      onClick={saveDefaultMessage}
                      disabled={savingDefault}
                      className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-[#0B2A36] text-white text-[9px] font-black uppercase tracking-widest disabled:opacity-50"
                    >
                      {savingDefault ? <RefreshCcw size={11} className="animate-spin" /> : <Save size={11} />} Guardar como predeterminado
                    </button>
                  )}
                  {message !== defaultMessage && (
                    <button type="button" onClick={() => setMessage(defaultMessage)} className="px-3 py-1.5 rounded-lg text-[9px] font-black uppercase tracking-widest text-slate-500 hover:bg-slate-100">
                      Volver al predeterminado
                    </button>
                  )}
                </div>
              </>
            ) : (
              <p className="text-[11px] text-slate-600 leading-snug whitespace-pre-line break-words">
                {message.trim() || defaultMessage}
              </p>
            )}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <button
              type="button"
              onClick={handlePrint}
              disabled={loading}
              className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 flex flex-col items-center gap-1.5 text-[10px] font-black uppercase tracking-widest text-slate-700 hover:bg-slate-100 disabled:opacity-50"
            >
              <Printer size={18} /> Imprimir / PDF
            </button>
            <button
              type="button"
              onClick={() => send({ targetPhone: '', clientName: '' })}
              disabled={loading || busy}
              className="rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 flex flex-col items-center gap-1.5 text-[10px] font-black uppercase tracking-widest text-emerald-700 hover:bg-emerald-100 disabled:opacity-50"
              title="Manda el PDF por WhatsApp eligiendo el contacto"
            >
              <MessageCircle size={18} /> WhatsApp (elegir contacto)
            </button>
          </div>
          <p className="text-[9px] font-bold text-slate-400 leading-snug">
            {isMobileDevice()
              ? 'Sin número: se abre el menú de compartir con el PDF adjunto. Con número (cliente o tipeado abajo): se descarga el PDF y se abre ese chat; adjuntalo con el clip.'
              : 'WhatsApp: se descarga el PDF y se abre el chat con el mensaje listo; arrastrá el archivo al chat y enviá.'}
          </p>

          {/* ── Mandar a un cliente ── */}
          <div className="space-y-2 border-t border-slate-100 pt-4">
            <div className="flex items-center gap-2 text-[9px] font-black uppercase tracking-widest text-slate-500">
              <Users size={14} /> Mandar a un cliente o número
            </div>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-300" size={14} />
              <input
                type="text"
                value={clientSearch}
                onChange={(e) => { setClientSearch(e.target.value); setSelectedClient(null); }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && typedPhone && !busy && !loading) {
                    e.preventDefault();
                    send({ targetPhone: clientSearch, clientName: '' });
                  }
                }}
                placeholder="BUSCAR CLIENTE O ESCRIBIR UN NÚMERO..."
                className="w-full bg-slate-50 border border-slate-100 pl-9 pr-3 py-2.5 rounded-xl text-[10px] font-black uppercase outline-none focus:border-[#51A7FD]"
              />
            </div>
            {clientSearch.trim().length < 2 && listClients.length > 0 && (
              <p className="text-[8px] font-black uppercase tracking-widest text-slate-400 ml-1">Clientes con esta lista asignada</p>
            )}

            {selectedClient ? (
              <div className="rounded-2xl border border-amber-200 bg-amber-50 p-3 space-y-2">
                <p className="text-[10px] font-bold text-amber-800">
                  {selectedClient.full_name} no tiene teléfono cargado. Ingresalo para mandarle la lista:
                </p>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-300" size={14} />
                  <input
                    type="tel"
                    autoFocus
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    placeholder="Ej: 341 555 1234"
                    className="w-full bg-white border border-amber-200 pl-9 pr-3 py-2.5 rounded-xl text-[11px] font-bold outline-none"
                  />
                </div>
                <label className="flex items-center gap-2 text-[9px] font-black uppercase tracking-widest text-amber-800 cursor-pointer">
                  <input type="checkbox" className="w-4 h-4 accent-amber-500" checked={savePhone} onChange={(e) => setSavePhone(e.target.checked)} />
                  Guardarlo en la ficha del cliente
                </label>
                <div className="flex gap-2">
                  <button
                    type="button"
                    disabled={busy || !normalizeWhatsAppPhone(phone)}
                    onClick={() => send({ targetPhone: phone, clientName: selectedClient.full_name, client: selectedClient })}
                    className="flex-1 rounded-xl bg-[#25D366] text-white py-2.5 text-[10px] font-black uppercase tracking-widest flex items-center justify-center gap-2 disabled:opacity-50"
                  >
                    {busy ? <RefreshCcw size={14} className="animate-spin" /> : <MessageCircle size={14} />} Mandar
                  </button>
                  <button type="button" onClick={() => setSelectedClient(null)} className="px-3 rounded-xl border border-amber-200 text-[10px] font-black uppercase text-amber-800">
                    Cancelar
                  </button>
                </div>
              </div>
            ) : (
              <div className="max-h-56 overflow-y-auto rounded-2xl border border-slate-100 divide-y divide-slate-100">
                {typedPhone && (
                  <button
                    type="button"
                    disabled={loading || busy}
                    onClick={() => send({ targetPhone: clientSearch, clientName: '' })}
                    className="w-full flex items-center justify-between gap-3 px-3 py-2.5 text-left bg-emerald-50/70 hover:bg-emerald-100/70 disabled:opacity-50"
                  >
                    <div className="min-w-0">
                      <div className="text-[10px] font-black uppercase text-emerald-800 truncate">Mandar al {clientSearch.trim()}</div>
                      <div className="text-[9px] font-bold text-emerald-700/70">Número tipeado · Enter para enviar</div>
                    </div>
                    {busy ? <RefreshCcw size={14} className="animate-spin text-emerald-400" /> : <Phone size={16} className="text-[#25D366] shrink-0" />}
                  </button>
                )}
                {shownClients.map((client) => {
                  const hasPhone = !!normalizeWhatsAppPhone(client.phone);
                  return (
                    <button
                      key={client.id}
                      type="button"
                      disabled={loading || busy}
                      onClick={() => handleClientClick(client)}
                      className="w-full flex items-center justify-between gap-3 px-3 py-2.5 text-left hover:bg-emerald-50/50 disabled:opacity-50"
                    >
                      <div className="min-w-0">
                        <div className="text-[10px] font-black uppercase text-slate-700 truncate">{client.full_name || 'Sin nombre'}</div>
                        <div className={`text-[9px] font-bold ${hasPhone ? 'text-slate-400' : 'text-amber-600'}`}>
                          {hasPhone ? client.phone : 'Sin teléfono: tocá para cargarlo'}
                        </div>
                      </div>
                      {busy ? <RefreshCcw size={14} className="animate-spin text-slate-300" /> : <MessageCircle size={16} className={hasPhone ? 'text-[#25D366] shrink-0' : 'text-slate-300 shrink-0'} />}
                    </button>
                  );
                })}
                {shownClients.length === 0 && !typedPhone && (
                  <div className="px-3 py-6 text-center text-[9px] font-black uppercase tracking-widest text-slate-300">
                    {clientSearch.trim().length >= 2 ? 'Sin clientes con ese nombre' : 'Buscá un cliente por nombre'}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
